// @ts-check
/* global $ module require plupload */
'use_strict';

declare var $;
declare var Tygh;
declare var plupload;

import { GenericUploadQueue } from './generic_upload_queue';
import { Notifications } from '../tv_utils/notifications';

class DIFUUploadAdditionalQueue extends GenericUploadQueue {

    /**
     * * Included from GenericUploadQueue.
     * @var {[key: string]: any} form_elements dict of related HTMLElements on the dom.
     * @var {string} dispatch url to call once to upload files.
     * @var {string} container changes based on difu.
     *
     * * Additional variables needed for DIFU Additional Uploads.
     * @var {string} uploader_id is the Unique ID for each Design to Upload for.
     * @var {string} order_id is either the Config ID or Inventtrans ID.
     * @var {number} sales_id is needed for an Order and also determines if this is from an order.
     * @var {boolean} from_order determines if this is from a product or from an order.
     * @var {any} upload_queue is the plupload queue initialized.
     * @var {[key: string]: any} difu_data is the data needed for the Design It For You feature.
     * @var {number} items_to_upload is a flag for and number of items needed to upload.
     * @var {boolean} first_open is a flag for when the Uploader is opened the first time.
     * @var {string} switch_text is the custom text the Uploader Switch Button has at first.
     * @var {boolean} switch_open is a flag to indicate if the Uploader is open or closed.
     * @var {Element} parent is the Parent element that contains all the inputs we need.
     */
    form_elements: { [key: string]: any } = {};
    dispatch: string;
    container: string = 'upload_queue';

    uploader_id: string;
    order_id: string;
    sales_id: string;
    from_order: boolean = false;
    upload_queue: any;
    difu_data: { [key: string]: any } = {};
    items_to_upload: number = 0;
    first_open: boolean = true;
    switch_text: string = '';
    switch_open: boolean = false;
    parent: Element;

    /**
     * @override
     * Overriding the Constructor to include information necessary for Design It For You.
     *
     * @param {string} uploader_id is the Element that contains the Uploader ID.
     */
    constructor(uploader_id: HTMLInputElement) {
        super();

        // Because this is Additional Artwork we need to get the parent that contains the necessary elements.
        this.parent = <HTMLInputElement>uploader_id.parentElement.parentElement.parentElement;

        this.uploader_id = uploader_id.value;
        this.container = `${this.container}_${this.uploader_id}`;

        const config_id = (<HTMLInputElement>this.parent.querySelector(
            `.js-tv-upload-artwork-optional-configuration-id-${this.uploader_id}`
        )).value;
        const inventtrans_id = (<HTMLInputElement>this.parent.querySelector(
            `.js-tv-upload-artwork-optional-inventtrans-id-${this.uploader_id}`
        )).value;
        this.order_id = config_id ? config_id : inventtrans_id ? inventtrans_id : '';

        const uploader_container = this.parent.querySelector(`#${this.container}`);
        // Return early if queue container or data doesn't exist on the page.
        if (!uploader_container || !this.order_id) {
            return;
        }
        // Clone the Uploader to remove all Event Listeners to stop multiple add files requests.
        const new_uploader = uploader_container.cloneNode(true);
        uploader_container.parentNode.replaceChild(new_uploader, uploader_container);
        this.form_elements.uploader_container = new_uploader;

        this.dispatch = `${window.location.origin}/index.php?dispatch=tv_artwork.upload_additional_difu`;
        this.sales_id = inventtrans_id ? (<HTMLInputElement>this.parent.querySelector(
            `.js-tv-upload-artwork-optional-sales-id-${this.uploader_id}`
        )).value : '';
        this.from_order = this.sales_id ? true : false;

        this.__init_queue();
        this.__bind();
    }

    /**
     * Creates an instance of the plupload queue.
     *
     * @returns {void}
     */
    __init_queue(): void {
        $(this.form_elements.uploader_container).pluploadQueue({
            runtimes: 'html5',
            max_file_size: '1536mb',
            chunk_size: '5mb',
            dragdrop: true,
            container: this.container,
            url: this.dispatch,
            flash_swf_url: 'js/addons/tv_artwork/Moxie.swf',
            silverlight_xap_url: 'js/addons/tv_artwork/Moxie.xap',
            filters: [],
            rename: true,
            sortable: true,
            init: {
                BeforeUpload: this.validate_upload.bind(this),
                UploadComplete: this.upload_complete.bind(this),
                FileFiltered: super.file_filter.bind(this),
                Error: super.handle_errors.bind(this)
            }

        });
    }

    /**
     * New bind method to change class variables to DIFU based variables.
     *
     * @returns {void}
     */
    __bind(): void {
        this.bind_plupload();

        // Make the entire Plupload Queue a button.
        this.form_elements.uploader_container.addEventListener('click', () => {
            this.form_elements.add_files.click();
        });

        // This is the button to open the uploader.
        this.form_elements.uploader_switch = this.parent.querySelector(
            `#js-tv-upload-artwork-optional-button-${this.uploader_id}`
        );
        if (this.form_elements.uploader_switch) {
            this.form_elements.uploader_switch.classList.remove('hidden');
            this.form_elements.uploader_switch.addEventListener('click', this.open_close_uploader.bind(this));
            this.switch_text = this.form_elements.uploader_switch.innerHTML;
        }

        // Container that holds the Uploader.
        this.form_elements.optional_uploader = this.parent.querySelector(
            `#js-tv-upload-artwork-optional-uploader-${this.uploader_id}`
        );

        // The Chat Log Body.
        this.form_elements.chat_log_body = this.parent.querySelector(`#js-chat-log-init-${this.uploader_id}`);

        // Clone the submit button.
        this.form_elements.message = this.parent.querySelector(`.js-chat-log-message-${this.uploader_id}`);
        this.form_elements.custom_validation_message = document.getElementById('js-custom-validation-message');
        this.form_elements.submit_button = this.parent.querySelector(`.js-chat-log-button-submit-${this.uploader_id}`);
        this.form_elements.clone_submit = this.parent.querySelector(
            `#js-tv-upload-artwork-optional-submit-${this.uploader_id}`
        );
        this.form_elements.submit_button.classList.add('hidden');
        this.form_elements.clone_submit.classList.remove('hidden');
        this.form_elements.clone_submit.addEventListener('click', () => {
            // If there are items to upload, do that before submitting the message.
            if (this.items_to_upload) {
                this.form_elements.start_upload.click();
            } else {
                // If there are no items to upload, close the uploader if necessary and submit the message.
                if (this.switch_open) {
                    this.open_close_uploader();
                }
                this.form_elements.submit_button.click();
            }
        });

    }

    /**
     * Method to bind plupload specific elements and make styling changes as needed.
     *
     * @returns {void}
     */
    bind_plupload(): void {
        // pluploadQueue.
        this.upload_queue = $(this.form_elements.uploader_container).pluploadQueue();
        const uploader_container = this.form_elements.uploader_container;

        // plupload Elements.
        this.form_elements.button_container = uploader_container.querySelector(`.plupload_buttons`);
        this.form_elements.upload_status = uploader_container.querySelector(`.plupload_upload_status`);
        this.form_elements.start_upload = uploader_container.querySelector(`.plupload_start`);

        this.form_elements.add_files = uploader_container.querySelector(`#${this.container}_browse`);
        // Hide plupload buttons for custom UI effects and Upload Logic.
        this.form_elements.add_files.style.display = 'none';

        this.form_elements.filelist_container = uploader_container.querySelector(`#${this.container}_filelist`);
        this.form_elements.filelist_container.classList.add('tv-height__size-100px');

        // Removing tooltip.
        uploader_container.querySelector(`#${this.container}_container`).removeAttribute('title');

        // Add items to the Items to Upload.
        this.upload_queue.bind('FilesAdded', (up, files) => {
            // Need to be sure the file wasn't filtered out.
            if (up.files.length > 0) {
                this.items_to_upload += 1;
            }

            this.adjust_select_files();
        });

        // Reduce the number of Items to Upload.
        this.upload_queue.bind('FilesRemoved', () => {
            this.items_to_upload -= 1;

            if (this.items_to_upload < 0) {
                this.items_to_upload = 0;
            }

            this.adjust_select_files();
        });

    }

    /**
     * Method to show the Uploader and change the Uploader Switch event listener and text.
     *
     * @return {void}
     */
    open_close_uploader(): void {
        if (this.first_open) {
    
            // Adding custom Drop Text.
            this.form_elements.filelist_container.insertAdjacentHTML('beforeend', `
                <div class="tv-display__flex tv-width__half tv-margin__auto tv-height__size-100px">
                    <div class="tv-margin__auto tv-text-align__center">Drag files here or Click to add files.</div>
                    <i class="fas fa-upload fa-5x tv-file__upload"></i>
                </div>`
            );

            // We need to make sure the Uploader is closed when the Modal is.
            const modal = $.ceDialog('get_last')[0];
            if (modal && modal.style.display === 'block') {
                modal.parentElement.querySelector('[title*="Close"').addEventListener('click', () => {
                    this.switch_open = true;
                    this.open_close_uploader();
                    this.first_open = true;
                });
            }

            this.first_open = false;
        }
        if (this.switch_open) {
            this.form_elements.optional_uploader.classList.add('hidden');
            this.form_elements.uploader_switch.innerHTML = this.switch_text;
            if (this.form_elements.chat_log_body) {
                this.form_elements.chat_log_body.classList.remove('tv-height__30vh');
            }
            this.switch_open = false;
        } else {
            this.form_elements.optional_uploader.classList.remove('hidden');
            this.form_elements.uploader_switch.innerHTML = Tygh.tr(
                'tv_upload_artwork_optional_hide_button'
            );
            if (this.form_elements.chat_log_body) {
                this.form_elements.chat_log_body.classList.add('tv-height__30vh');
            }
            this.switch_open = true;
        }
    }

    /**
     * Method to Customize Select Files such as Customizing the Drop Text and
     * stopping propagation (or bubbling) on the event of removing a file.
     *
     * @return {void}
     */
    adjust_select_files(): void {
        // Get container height.
        const container_height = this.form_elements.filelist_container.offsetHeight;

        // Get last child element position and height.
        const last_child = <HTMLElement>this.form_elements.filelist_container.lastChild;
        const min_height = last_child.offsetHeight;
        const vertical_offset = last_child.offsetTop + min_height;

        // Calculate max height of plus symbol.
        const height = Math.abs(container_height - vertical_offset);

        const drop_text_start = `
            <div class="tv-plupload-drop-text"
                style="height:${height}px;"
            ><div class="tv-margin__auto tv-text-align__center">
        `;

        // If there are Items to Upload, add the plus sign.
        if (this.items_to_upload) {
            // Add Plus symbol.
            this.form_elements.filelist_container.insertAdjacentHTML('beforeend', `
                ${drop_text_start}
                    <i class="ty-icon-plus"></i>
                </div></div>
            `);
        } else { // Otherwise we need to have the Drag or Click to add Files.
            // Adding custom Drop Text.
            this.form_elements.filelist_container.insertAdjacentHTML('beforeend', `
                ${drop_text_start}
                    Drag files here or Click to add files.</div>
                    <i class="fas fa-upload fa-5x tv-file__upload"></i>
                </div>
            `);
        }

        // Add click event to Remove File button to stop it from firing the Browse for File.
        for (const remove_file of document.querySelectorAll('.plupload_file_action a')) {
            remove_file.addEventListener('click', event => {
                event.stopPropagation();
            })
        }
    }

    /**
     * @override
     * A complete Override of the Validate Upload method to pass Design It For You specific information.
     *
     * @returns {boolean}
     */
    validate_upload(): boolean {
        // Trim the message.
        const message = this.form_elements.message.value.trim();
        this.form_elements.message.value = message;

        // Validate the message.
        if (message.length < 1) {
            if (this.items_to_upload) {
                this.form_elements.message.setCustomValidity(this.form_elements.custom_validation_message.value);

                this.form_elements.button_container.style.display = 'inline';
                this.form_elements.upload_status.style.display = 'inline';

                this.upload_queue.state = plupload.STOPPED;
                this.upload_queue.status = plupload.QUEUED;
                this.upload_queue.trigger('StateChanged');
            }

            this.form_elements.message.reportValidity();
            return false;
        }

        // Get the data for the DIFU.
        this.difu_data = {
            'sales_id': this.sales_id,
            'inventtrans_id': this.from_order ? this.order_id : '',
            'configuration_id': !this.from_order ? this.order_id : ''
        };

        // Set parameters for the artwork Upload.
        this.upload_queue.settings.multipart_params = this.difu_data;

        // Show the loading spinner
        $.toggleStatusBox('show', {
            overlay: true,
            statusContent: '<span class="ty-ajax-loading-box-with__text-wrapper">Uploading Artwork</span>',
            statusClass: 'ty-ajax-loading-box_text_block'
        });

        return true;
    }

    /**
     * @override
     * Calls parent method Upload Complete, then adds extra logic for Design It For You.
     *
     * @param {plupload.Uploader} uploader_instance for plupload.
     * @param {array} files array of the files that have been uploaded.
     * @returns {void}
     */
    upload_complete(uploader_instance, files): void {
        $.toggleStatusBox('hide');
        let error_found = false;
        this.items_to_upload = 0;
        // Check to see if the file failed upload and display an error notification.
        for (const file of files) {
            if (file.status === plupload.FAILED) {
                Notifications.set_notification(
                    'E',
                    'Failed to upload artwork.',
                    `An error occurred when uploading ${file.name}`,
                    false
                );
                error_found = true;
                uploader_instance.removeFile(file);
            }
        }

        if (!error_found) {
            // Close Uploader.
            this.open_close_uploader();
    
            // Reset and re-bind Uploader.
            super.init_queue();
            this.bind_plupload();

            // Reset Flags.
            this.first_open = true;

            // After uploading, if there is a message entered attempt to submit it.
            if (this.form_elements.message.value.trim().length > 0) {
                this.form_elements.submit_button.click();
            }
        }
    }

}

// Exported Function to Initialize which Additional Artwork Uploader is needed.
export function init_additional_uploads(upload_additional_queue_id: HTMLInputElement): void {
    if (upload_additional_queue_id.dataset.uploadType &&
        upload_additional_queue_id.dataset.uploadType === 'difu'
    ) {
        (() => new DIFUUploadAdditionalQueue(upload_additional_queue_id))();
    }
}
